import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { erc20Abi, poolAbi } from '../../shared/abis';
import { MARKET } from '../../shared/market.generated';
import type { UserSnapshot } from '../../shared/reader';
import type { ReserveSnapshot } from '../../shared/types';
import { useEthClaim, type EthClaimPhase } from '../data/ethfaucet';
import { useEthFaucet, useMarket, useUser } from '../data/queries';
import { explorerTx, useTx, type TxPhase } from '../data/tx';
import { explainError } from '../lib/errors';
import { fmtEth } from '../../shared/units';
import { faucetRequest } from '../lib/faucet';
import { fmtPct, fmtToken, fmtUsd, parseAmount, rawToInput, rayToApy, usdValue } from '../lib/format';
import { MAX_UINT, countsAsCollateral, maxFor, positionOf, simulate, type ActionKind, type Position } from '../lib/position';
import type { WalletOption } from '../wallet/eip6963';
import { useWallet } from '../wallet/WalletProvider';
import { AssetIcon } from './AssetBadge';
import { Dialog } from './Dialog';
import { IconArrowRight, IconCheck, IconChevronDown, IconMail, IconSpinner, IconWallet } from './Icons';
import { Turnstile } from './Turnstile';
import { Alert, ExternalLink, HealthFactor } from './ui';

type ModalState =
  | { type: 'action'; kind: ActionKind; symbol: string }
  | { type: 'collateral'; symbol: string }
  | { type: 'faucet'; symbol: string }
  | { type: 'faucet-eth' }
  | { type: 'risk' }
  | { type: 'connect' }
  | null;

interface ModalsApi {
  openAction(kind: ActionKind, symbol: string): void;
  openCollateral(symbol: string): void;
  openFaucet(symbol: string): void;
  /** The ETH gas drip alone (human check, sent by the faucet). */
  openEthFaucet(): void;
  openRisk(): void;
  openConnect(): void;
}
const Ctx = createContext<ModalsApi | null>(null);
export function useModals(): ModalsApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useModals outside ModalsProvider');
  return v;
}

const VERB: Record<ActionKind, string> = { supply: 'Supply', withdraw: 'Withdraw', borrow: 'Borrow', repay: 'Repay' };
const PAST: Record<ActionKind, string> = { supply: 'Supplied', withdraw: 'Withdrew', borrow: 'Borrowed', repay: 'Repaid' };
const BALANCE_LABEL: Record<ActionKind, string> = { supply: 'Wallet balance', withdraw: 'Supply balance', borrow: 'Available', repay: 'Wallet balance' };
const LIMIT_HINT: Partial<Record<string, string>> = {
  health: 'Limited so that your health factor stays above 1.02.',
  liquidity: 'Limited by the liquidity left in the reserve.',
  cap: 'Limited by the cap of this reserve.',
  power: '99% of your borrowing power, leaving a small buffer against price moves.',
  debt: 'Repays the full debt, including the interest accruing until the transaction lands.',
};

/* ---------- shared building blocks ---------- */

function OverviewRow({ label, children, sub }: { label: string; children: ReactNode; sub?: string }) {
  return (
    <div className="ov-row">
      <span>{label}</span>
      <span className="ov-row__value">{children}{sub && <small>{sub}</small>}</span>
    </div>
  );
}

function HfChange({ before, after }: { before: Position; after: Position | null }) {
  return (
    <OverviewRow label="Health factor" sub="Liquidation at <1.0">
      <span className="change">
        <HealthFactor value={before.healthFactor} />
        {after && after.healthFactor !== before.healthFactor && <><IconArrowRight size={13} /><HealthFactor value={after.healthFactor} /></>}
      </span>
    </OverviewRow>
  );
}

function Success({ title, children, hash, onClose, extra }: { title: string; children: ReactNode; hash: string; onClose(): void; extra?: ReactNode }) {
  return (
    <Dialog title={title} onClose={onClose}>
      <div className="tx-success">
        <span className="tx-success__icon"><IconCheck size={26} /></span>
        <h3>All done!</h3>
        <p>{children}</p>
        {extra}
      </div>
      <div className="tx-actions">
        <ExternalLink href={explorerTx(hash)} className="review-link">Review tx details</ExternalLink>
        <button type="button" className="btn btn--primary btn--block btn--lg" onClick={onClose}>OK, close</button>
      </div>
    </Dialog>
  );
}

const phaseLabel = (phase: TxPhase | 'approving', idle: string, symbol: string): string =>
  phase === 'approving' ? `Approving ${symbol}…` : phase === 'simulating' || phase === 'wallet' ? 'Confirm in your wallet…' : phase === 'pending' ? 'Waiting for confirmation…' : idle;

/* ---------- supply / withdraw / borrow / repay ---------- */

function ActionModal({ kind, reserve, user, onClose }: { kind: ActionKind; reserve: ReserveSnapshot; user: UserSnapshot; onClose(): void }) {
  const { wrongNetwork, switchNetwork, watchAsset, walletKind } = useWallet();
  const { send } = useTx();
  const [text, setText] = useState('');
  const [useMax, setUseMax] = useState(false);
  const [phase, setPhase] = useState<TxPhase | 'approving'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [ack, setAck] = useState(false);
  const [done, setDone] = useState<{ hash: string; amount: bigint } | null>(null);

  const ur = user.reserves[reserve.symbol];
  const position = useMemo(() => positionOf(user), [user]);
  const max = useMemo(() => maxFor(kind, position, reserve, ur), [kind, position, reserve, ur]);
  // Repay is bounded by the debt, but the figure people look for next to the input is what the wallet holds.
  const shownBalance = kind === 'repay' ? (ur?.walletBalance ?? 0n) : max.amount;
  const amount = useMax ? max.amount : parseAmount(text, reserve.decimals);
  const price = BigInt(reserve.priceUsd);
  const after = amount && amount > 0n ? simulate(position, reserve, ur, kind, amount) : null;
  const exceeds = !!amount && amount > max.amount;
  const liquidatable = !!after && after.debtUsd > 0 && after.healthFactor < 1;
  const risky = !!after && !liquidatable && (kind === 'borrow' || kind === 'withdraw') && after.debtUsd > 0 && after.healthFactor < 1.5;
  const needsApproval = (kind === 'supply' || kind === 'repay') && !!amount && amount > 0n && (ur?.allowance ?? 0n) < amount;
  const busy = phase !== 'idle';
  const ready = !!amount && amount > 0n && !exceeds && !liquidatable && (!risky || ack);
  const affectsHealth = kind === 'borrow' || kind === 'repay' || countsAsCollateral(reserve, ur);
  const title = `${VERB[kind]} ${reserve.symbol}`;
  const dp = reserve.decimals === 6 ? 2 : 4;

  async function approve() {
    if (!amount) return;
    setError(null);
    setPhase('approving');
    // Exact-amount approvals. Repaying everything needs headroom for the interest accruing until inclusion.
    const allowance = kind === 'repay' && useMax && max.useMaxUint ? amount + amount / 200n + 1n : amount;
    const r = await send({ label: `Approve ${reserve.symbol}`, address: reserve.address, abi: erc20Abi, functionName: 'approve', args: [MARKET.pool, allowance] });
    setPhase('idle');
    if (!r.ok) setError(r.error);
  }

  async function submit() {
    if (!amount || !ready) return;
    setError(null);
    const me = user.address;
    const wire = useMax && max.useMaxUint ? MAX_UINT : amount;
    const args: Record<ActionKind, readonly unknown[]> = {
      supply: [reserve.address, amount, me, 0],
      withdraw: [reserve.address, wire, me],
      borrow: [reserve.address, amount, 2n, 0, me],
      repay: [reserve.address, wire, 2n, me],
    };
    const r = await send({ label: title, address: MARKET.pool, abi: poolAbi, functionName: kind, args: args[kind] }, setPhase);
    setPhase('idle');
    if (r.ok) setDone({ hash: r.hash, amount }); else setError(r.error);
  }

  if (done) {
    return (
      <Success title={title} hash={done.hash} onClose={onClose}
        extra={kind === 'supply' && walletKind !== 'embedded' ? (
          <button type="button" className="btn btn--outlined btn--sm" onClick={() => void watchAsset({ address: reserve.aToken, symbol: `a${reserve.symbol}`.slice(0, 11), decimals: reserve.decimals }).catch(() => undefined)}>
            <IconWallet size={14} />Add a{reserve.symbol} to wallet to track your balance
          </button>
        ) : undefined}>
        You {PAST[kind]} <strong>{fmtToken(done.amount, reserve.decimals, dp)} {reserve.symbol}</strong>
      </Success>
    );
  }

  return (
    <Dialog title={title} onClose={busy ? () => undefined : onClose}>
      {wrongNetwork && <Alert tone="warning" action={<button type="button" className="btn btn--outlined btn--sm" onClick={() => void switchNetwork().catch((e) => setError(explainError(e)))}>Switch network</button>}>Please switch to Robinhood Chain Testnet.</Alert>}

      <div className="field">
        <span className="field__label">Amount</span>
        <label className="amount" data-invalid={exceeds || undefined}>
          <span className="amount__row">
            <input inputMode="decimal" autoComplete="off" data-autofocus placeholder="0.00" aria-label="Amount" disabled={busy}
              value={useMax ? rawToInput(max.amount, reserve.decimals) : text}
              onChange={(e) => { setUseMax(false); setAck(false); setText(e.target.value.replace(/[^\d.,]/g, '')); }} />
            <span className="amount__asset"><AssetIcon symbol={reserve.symbol} size={24} />{reserve.symbol}</span>
          </span>
          <span className="amount__bottom">
            <span>{amount ? fmtUsd(usdValue(amount, reserve.decimals, price)) : '$0.00'}</span>
            <span>{BALANCE_LABEL[kind]} {fmtToken(shownBalance, reserve.decimals, dp)}
              <button type="button" className="max-btn" disabled={busy || max.amount === 0n} onClick={() => { setUseMax(true); setAck(false); }}>MAX</button>
            </span>
          </span>
        </label>
        {exceeds && <p className="field__error">The amount exceeds the maximum of {fmtToken(max.amount, reserve.decimals, dp)} {reserve.symbol}.</p>}
        {useMax && LIMIT_HINT[max.limitedBy] && <p className="field__hint">{LIMIT_HINT[max.limitedBy]}</p>}
      </div>

      <div className="field">
        <span className="field__label">Transaction overview</span>
        <div className="overview">
          {kind === 'supply' && <>
            <OverviewRow label="Supply APY">{fmtPct(rayToApy(reserve.supplyRateRay))}</OverviewRow>
            <OverviewRow label="Collateralization">{countsAsCollateral(reserve, ur) ? <span className="text-good">Enabled</span> : <span className="text-critical">{reserve.usageAsCollateralEnabled ? 'Disabled' : 'Unavailable'}</span>}</OverviewRow>
          </>}
          {kind === 'withdraw' && <OverviewRow label="Remaining supply">{fmtToken((ur?.supplied ?? 0n) - (amount && amount < (ur?.supplied ?? 0n) ? amount : amount ? (ur?.supplied ?? 0n) : 0n), reserve.decimals, dp)} {reserve.symbol}</OverviewRow>}
          {kind === 'borrow' && <OverviewRow label="Borrow APY">{fmtPct(rayToApy(reserve.borrowRateRay))}</OverviewRow>}
          {kind === 'repay' && (
            <OverviewRow label="Remaining debt">
              <span className="change">{fmtToken(ur?.debt ?? 0n, reserve.decimals, dp)} {reserve.symbol}
                {amount ? <><IconArrowRight size={13} />{fmtToken((ur?.debt ?? 0n) > amount ? (ur?.debt ?? 0n) - amount : 0n, reserve.decimals, dp)} {reserve.symbol}</> : null}
              </span>
            </OverviewRow>
          )}
          {affectsHealth && <HfChange before={position} after={after} />}
        </div>
      </div>

      {liquidatable && <Alert tone="critical">This would make your position liquidatable. Choose a smaller amount.</Alert>}
      {risky && (
        <>
          <Alert tone="warning">{kind === 'borrow' ? 'Borrowing' : 'Withdrawing'} this amount will reduce your health factor and increase the risk of liquidation.</Alert>
          <label className="ack"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />I acknowledge the risks involved.</label>
        </>
      )}
      {error && <Alert tone="critical">{error}</Alert>}

      <div className="tx-actions">
        {needsApproval && (
          <button type="button" className="btn btn--primary btn--block btn--lg" disabled={!ready || busy || wrongNetwork} onClick={() => void approve()}>
            {phase === 'approving' && <IconSpinner />}{phaseLabel(phase === 'approving' ? phase : 'idle', `Approve ${reserve.symbol} to continue`, reserve.symbol)}
          </button>
        )}
        <button type="button" className="btn btn--primary btn--block btn--lg" disabled={!ready || busy || needsApproval || wrongNetwork} onClick={() => void submit()}>
          {busy && phase !== 'approving' && <IconSpinner />}{!amount || amount === 0n ? 'Enter an amount' : phaseLabel(phase === 'approving' ? 'idle' : phase, title, reserve.symbol)}
        </button>
      </div>
    </Dialog>
  );
}

/* ---------- usage as collateral ---------- */

function CollateralModal({ reserve, user, onClose }: { reserve: ReserveSnapshot; user: UserSnapshot; onClose(): void }) {
  const { send } = useTx();
  const [phase, setPhase] = useState<TxPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const ur = user.reserves[reserve.symbol];
  const enable = !(ur?.usedAsCollateral ?? false);
  const position = positionOf(user);
  const supplied = ur?.supplied ?? 0n;
  // Toggling moves the whole supplied balance in or out of the collateral set.
  const after = enable
    ? simulate(position, reserve, { ...ur!, supplied: 0n }, 'supply', supplied)
    : simulate(position, reserve, ur, 'withdraw', supplied);
  const blocked = !enable && after.debtUsd > 0 && after.healthFactor < 1;
  const title = `${enable ? 'Enable' : 'Disable'} ${reserve.symbol} as collateral`;

  if (done) return <Success title="Review tx" hash={done} onClose={onClose}>Your <strong>{reserve.symbol}</strong> is now {enable ? 'used' : 'not used'} as collateral.</Success>;
  return (
    <Dialog title={`Review tx ${reserve.symbol}`} onClose={phase === 'idle' ? onClose : () => undefined}>
      <Alert tone={enable ? 'info' : 'warning'}>
        {enable ? 'Enabling this asset as collateral increases your borrowing power and health factor. However, it can get liquidated if your health factor drops below 1.'
          : 'Disabling this asset as collateral affects your borrowing power and health factor.'}
      </Alert>
      <div className="field">
        <span className="field__label">Transaction overview</span>
        <div className="overview">
          <OverviewRow label="Supply balance">{fmtToken(supplied, reserve.decimals)} {reserve.symbol}</OverviewRow>
          <HfChange before={position} after={after} />
        </div>
      </div>
      {blocked && <Alert tone="critical">You cannot disable this collateral: it would make your position liquidatable.</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}
      <div className="tx-actions">
        <button type="button" className="btn btn--primary btn--block btn--lg" disabled={blocked || phase !== 'idle'}
          onClick={async () => {
            setError(null);
            const r = await send({ label: title, address: MARKET.pool, abi: poolAbi, functionName: 'setUserUseReserveAsCollateral', args: [reserve.address, enable] }, setPhase);
            setPhase('idle');
            if (r.ok) setDone(r.hash); else setError(r.error);
          }}>
          {phase !== 'idle' && <IconSpinner />}{phaseLabel(phase, title, reserve.symbol)}
        </button>
      </div>
    </Dialog>
  );
}

/* ---------- faucet ---------- */

export function fmtCooldown(sec: number): string {
  if (sec <= 0) return 'no cooldown';
  if (sec % 86_400 === 0) return `${sec / 86_400} day${sec === 86_400 ? '' : 's'}`;
  if (sec % 3600 === 0) return `${sec / 3600} hour${sec === 3600 ? '' : 's'}`;
  return `${Math.ceil(sec / 60)} min`;
}

const faucetAmount = (r: ReserveSnapshot): string => `${fmtToken(BigInt(r.faucet.amount), r.decimals, 0)} ${r.symbol}`;
const ethPhaseLabel = (phase: EthClaimPhase, idle: string): string => (phase === 'requesting' ? 'Asking the faucet…' : phase === 'pending' ? 'Waiting for confirmation…' : idle);

function FaucetModal({ reserve, onClose }: { reserve: ReserveSnapshot; onClose(): void }) {
  const { send } = useTx();
  const [phase, setPhase] = useState<TxPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const title = `Faucet ${reserve.symbol}`;
  const stock = reserve.faucet.kind === 'stock';
  const amountText = faucetAmount(reserve);
  if (done) return <Success title={title} hash={done} onClose={onClose}>You received <strong>{amountText}</strong></Success>;
  return (
    <Dialog title={title} onClose={phase === 'idle' ? onClose : () => undefined}>
      <div className="field">
        <span className="field__label">Transaction overview</span>
        <div className="overview">
          <OverviewRow label="Amount"><span className="change"><AssetIcon symbol={reserve.symbol} size={18} />{amountText}</span></OverviewRow>
          {stock && reserve.faucet.cooldown > 0 && <OverviewRow label="Next claim">after {fmtCooldown(reserve.faucet.cooldown)}</OverviewRow>}
        </div>
      </div>
      <p className="muted small">{stock
        ? `Minted by the market’s stock faucet, the only minter of this token besides the admin (minting is role-gated, like the mainnet tokens).${reserve.faucet.cooldown > 0 ? ' Each account can claim once per cooldown.' : ''}`
        : 'Freely mintable test token of this market.'}</p>
      {error && <Alert tone="critical">{error}</Alert>}
      <div className="tx-actions">
        <button type="button" className="btn btn--primary btn--block btn--lg" disabled={phase !== 'idle'}
          onClick={async () => {
            setError(null);
            const r = await send(faucetRequest(reserve, MARKET.stockTokens?.faucet, title), setPhase);
            setPhase('idle');
            if (r.ok) setDone(r.hash); else setError(r.error);
          }}>
          {phase !== 'idle' && <IconSpinner />}{phaseLabel(phase, title, reserve.symbol)}
        </button>
      </div>
    </Dialog>
  );
}

/** The ETH gas drip: the faucet sends it itself (no signature, no gas on the user's side) once the Turnstile human check passed. */
function EthFaucetModal({ onClose }: { onClose(): void }) {
  const { address } = useWallet();
  const info = useEthFaucet(address).data;
  const claimEth = useEthClaim();
  const [phase, setPhase] = useState<EthClaimPhase>('idle');
  const [token, setToken] = useState<string | null>(null);
  const [tsReset, setTsReset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const title = 'Faucet ETH';
  const amount = info ? `${fmtEth(BigInt(info.dripWei))} ETH` : 'ETH';
  if (done) return <Success title={title} hash={done} onClose={onClose}>You received <strong>{amount}</strong> for gas.</Success>;
  return (
    <Dialog title={title} onClose={phase === 'idle' ? onClose : () => undefined}>
      <div className="field">
        <span className="field__label">Transaction overview</span>
        <div className="overview">
          <OverviewRow label="Amount"><span className="change"><AssetIcon symbol="ETH" size={18} />{amount}</span></OverviewRow>
          {info && <OverviewRow label="Condition">wallet below {fmtEth(BigInt(info.maxBalanceWei))} ETH</OverviewRow>}
          {info && <OverviewRow label="Next claim">after {fmtCooldown(info.cooldownSeconds)}</OverviewRow>}
        </div>
      </div>
      <p className="muted small">The faucet sends the ETH itself, so you need neither a signature nor gas. Pass the human check, then claim.</p>
      {info?.siteKey && <Turnstile siteKey={info.siteKey} resetKey={tsReset} onToken={setToken} />}
      {info && !info.enabled && <Alert tone="warning">The ETH faucet is unavailable right now ({info.reason}). Use the official faucet instead.</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}
      <div className="tx-actions">
        <button type="button" className="btn btn--primary btn--block btn--lg" disabled={!address || !info?.enabled || !token || phase !== 'idle'}
          onClick={async () => {
            if (!address || !token) return;
            setError(null);
            const r = await claimEth(address, token, setPhase);
            setToken(null); setTsReset((k) => k + 1); // a Turnstile token is single-use
            setPhase('idle');
            if (r.ok) setDone(r.hash); else setError(r.error);
          }}>
          {phase !== 'idle' && <IconSpinner />}{ethPhaseLabel(phase, token ? `Claim ${amount}` : 'Pass the human check to continue')}
        </button>
      </div>
    </Dialog>
  );
}

/* ---------- liquidation risk parameters ---------- */

function Scale({ value, max, marks, tone }: { value: number; max: number; tone: string; marks: { at: number; label: string; tone?: string }[] }) {
  const pos = (v: number) => `${Math.min(100, Math.max(0, (v / max) * 100)).toFixed(2)}%`;
  return (
    <div className="scale">
      <div className="scale__track" data-tone={tone}>
        <div className="scale__fill" style={{ width: pos(value) }} />
        {marks.map((m) => <span key={m.label} className="scale__mark" data-tone={m.tone} style={{ left: pos(m.at) }} />)}
      </div>
      {/* a legend row rather than labels pinned under the marks, which can sit very close together */}
      <div className="scale__legend">{marks.map((m) => <span key={m.label}><i data-tone={m.tone} />{m.label}</span>)}</div>
    </div>
  );
}

function RiskModal({ user, onClose }: { user: UserSnapshot; onClose(): void }) {
  const p = positionOf(user);
  const hf = p.healthFactor;
  const tone = !Number.isFinite(hf) ? 'good' : hf >= 3 ? 'good' : hf < 1.1 ? 'critical' : 'warning';
  // Proximity to liquidation is linear in 1 / health factor, which keeps the scale bounded for any position.
  const proximity = Number.isFinite(hf) && hf > 0 ? Math.min(1, 1 / hf) : 0;
  return (
    <Dialog title="Liquidation risk parameters" onClose={onClose} wide>
      <p className="muted">Your health factor and loan to value determine the assurance of your collateral. To avoid liquidations you can supply more collateral or repay borrow positions.</p>
      <div className="risk-box">
        <div className="risk-box__head">
          <div><h3>Health factor</h3><p className="muted">Safety of your deposited collateral against the borrowed assets and its underlying value.</p></div>
          <HealthFactor value={hf} size="lg" />
        </div>
        <Scale value={proximity} max={1} tone={tone} marks={[{ at: 1, label: 'Liquidation value 1.00', tone: 'critical' }]} />
        <p className="muted small">The bar fills as the position approaches liquidation. If the health factor goes below 1, the liquidation of your collateral might be triggered.</p>
      </div>
      <div className="risk-box">
        <div className="risk-box__head">
          <div><h3>Current LTV</h3><p className="muted">Your current loan to value based on your collateral supplied.</p></div>
          <span className="hf hf--lg" data-tone={tone}>{fmtPct(p.currentLtv)}</span>
        </div>
        <Scale value={p.currentLtv} max={Math.max(p.liqThreshold * 1.15, 0.1)} tone={tone}
          marks={[{ at: p.maxLtv, label: `Max LTV ${fmtPct(p.maxLtv)}` }, { at: p.liqThreshold, label: `Liquidation threshold ${fmtPct(p.liqThreshold)}`, tone: 'critical' }]} />
        <p className="muted small">If your loan to value goes above the liquidation threshold your collateral supplied may be liquidated.</p>
      </div>
    </Dialog>
  );
}

/* ---------- wallet picker ---------- */

function ConnectModal({ onClose }: { onClose(): void }) {
  const { wallets, connect, status } = useWallet();
  const { notify } = useTx();
  const [error, setError] = useState<string | null>(null);
  const embedded = wallets.filter((w) => w.kind === 'embedded');
  const injected = wallets.filter((w) => w.kind === 'injected');
  const pick = (w: WalletOption) => {
    setError(null);
    if (w.kind === 'embedded') {
      // Privy opens its own sign-in window. This native <dialog> owns the top layer and would keep it inert, so it
      // closes first; a failure is reported as a toast instead of inline.
      onClose();
      connect(w).catch((e) => { if ((e as { code?: number }).code !== 4001) notify({ kind: 'error', title: 'Sign-in failed', body: explainError(e) }); }); // 4001 = closed the window
      return;
    }
    connect(w).then(onClose).catch((e) => setError(explainError(e)));
  };
  const row = (w: WalletOption) => (
    <li key={w.uuid}>
      <button type="button" className={w.kind === 'embedded' ? 'wallet-list__embedded' : undefined} disabled={status === 'connecting'} onClick={() => pick(w)}>
        <span><span>{w.name}</span>{w.hint && <small>{w.hint}</small>}</span>
        {w.icon ? <img src={w.icon} alt="" width={28} height={28} /> : <span className="wallet-list__fallback">{status === 'connecting' ? <IconSpinner /> : w.kind === 'embedded' ? <IconMail size={20} /> : <IconChevronDown size={16} style={{ transform: 'rotate(-90deg)' }} />}</span>}
      </button>
    </li>
  );
  return (
    <Dialog title="Connect a wallet" onClose={onClose}>
      {embedded.length > 0 && <ul className="wallet-list">{embedded.map(row)}</ul>}
      {injected.length > 0 ? (
        <>
          {embedded.length > 0 && <p className="muted small wallet-list__label">Browser wallets</p>}
          <ul className="wallet-list">{injected.map(row)}</ul>
        </>
      ) : embedded.length > 0 ? (
        <p className="muted small wallet-list__label">No browser wallet detected. To use an extension such as MetaMask or Rabby instead, install it and reload this page.</p>
      ) : (
        <div className="empty"><IconWallet size={28} /><strong>No wallet detected</strong><p className="muted">Install a browser wallet such as MetaMask or Rabby, then reload this page.</p></div>
      )}
      {error && <Alert tone="critical">{error}</Alert>}
      <p className="muted small">Wallets are provided by external providers. By connecting you confirm this is a testnet deployment whose tokens have no value.</p>
    </Dialog>
  );
}

/* ---------- provider ---------- */

export function ModalsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ModalState>(null);
  const { address } = useWallet();
  const market = useMarket().data;
  const user = useUser(address).data;
  const close = () => setState(null);
  const api = useMemo<ModalsApi>(() => ({
    openAction: (kind, symbol) => setState({ type: 'action', kind, symbol }),
    openCollateral: (symbol) => setState({ type: 'collateral', symbol }),
    openFaucet: (symbol) => setState({ type: 'faucet', symbol }),
    openEthFaucet: () => setState({ type: 'faucet-eth' }),
    openRisk: () => setState({ type: 'risk' }),
    openConnect: () => setState({ type: 'connect' }),
  }), []);
  const reserve = state && 'symbol' in state ? market?.reserves.find((r) => r.symbol === state.symbol) : undefined;

  return (
    <Ctx.Provider value={api}>
      {children}
      {state?.type === 'connect' && <ConnectModal onClose={close} />}
      {state?.type === 'risk' && user && <RiskModal user={user} onClose={close} />}
      {state?.type === 'action' && reserve && user && <ActionModal key={`${state.kind}-${state.symbol}`} kind={state.kind} reserve={reserve} user={user} onClose={close} />}
      {state?.type === 'collateral' && reserve && user && <CollateralModal reserve={reserve} user={user} onClose={close} />}
      {state?.type === 'faucet' && reserve && <FaucetModal reserve={reserve} onClose={close} />}
      {state?.type === 'faucet-eth' && <EthFaucetModal onClose={close} />}
    </Ctx.Provider>
  );
}
